import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../api/axios';
import { SearchIcon, ChevronDownIcon, CheckIcon } from './Icons';

export default function CountrySelect({ value, onChange, disabled = false, preload = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [suggestedCountry, setSuggestedCountry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);

  // Fetch countries from backend API
  const fetchCountries = useCallback(async (pageNum = 1, search = '', append = false, signal = null) => {
    try {
      setLoading(true);
      const res = await axios.get('/auth/country-codes', {
        params: {
          page: pageNum,
          per_page: 20,
          search: search,
        },
        signal,
      });

      const newItems = res.data?.data || [];
      const moreAvailable = res.data?.has_more ?? false;
      
      if (pageNum === 1 && !search && res.data?.suggested_country) {
        setSuggestedCountry(res.data.suggested_country);
      }

      setCountries(prev => (append ? [...prev, ...newItems] : newItems));
      setHasMore(moreAvailable);
      setPage(pageNum);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch countries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load only when dropdown opens or preload is requested to prevent duplicate API calls
  useEffect(() => {
    const controller = new AbortController();
    if ((isOpen || preload) && countries.length === 0) {
      fetchCountries(1, '', false, controller.signal);
    }
    return () => controller.abort();
  }, [isOpen, preload, countries.length, fetchCountries]);

  // Keep selected country synchronized with value prop
  useEffect(() => {
    if (!value) return;
    const matched = countries.find(c => c.iso2 === value);
    if (matched) {
      setSelectedCountry(matched);
    } else if (value && !selectedCountry) {
      setSelectedCountry({ iso2: value, name: value, flag: '🌐' });
    }
  }, [value, countries]);

  // Set default selection when countries load
  useEffect(() => {
    if (!value && countries.length > 0 && !selectedCountry) {
      const defaultItem = suggestedCountry || countries.find(c => c.iso2 === 'US') || countries[0];
      setSelectedCountry(defaultItem);
      if (onChange) onChange(defaultItem);
    }
  }, [countries, value, selectedCountry, onChange, suggestedCountry]);

  // Search input change handler
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchCountries(1, query, false);
  };

  // Scroll listener for infinite scrolling
  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 30 && hasMore && !loading) {
      fetchCountries(page + 1, searchQuery, true);
    }
  };

  // Click outside listener to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (country) => {
    setSelectedCountry(country);
    if (onChange) onChange(country);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block w-full text-left" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
      >
        <span className="flex items-center space-x-2 truncate">
          <span className="text-base">{selectedCountry?.flag || '🌐'}</span>
          <span className="font-medium text-slate-800 dark:text-slate-200 truncate">
            {selectedCountry?.name || selectedCountry?.iso2 || 'Select Country'}
          </span>
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 w-72 max-h-72 z-50 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden flex flex-col">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <div className="relative">
              <SearchIcon className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search country..."
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name="country_search_no_fill_addr"
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="overflow-y-auto flex-1 divide-y divide-slate-100 dark:divide-slate-700/50"
          >
            {suggestedCountry && !searchQuery && page === 1 && (
              <div className="mb-1 border-b-2 border-indigo-100 dark:border-indigo-900/30">
                <div className="px-3 py-1 text-[10px] uppercase font-bold text-indigo-500 tracking-wider">Suggested</div>
                <button
                  type="button"
                  onClick={() => handleSelect(suggestedCountry)}
                  className="w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors bg-indigo-50/50 dark:bg-indigo-950/20 hover:bg-slate-50 dark:hover:bg-slate-700/40 text-slate-700 dark:text-slate-300"
                >
                  <div className="flex items-center space-x-2 truncate">
                    <span className="text-base">{suggestedCountry.flag}</span>
                    <span className="truncate font-semibold text-slate-900 dark:text-slate-100">
                      {suggestedCountry.name}
                    </span>
                  </div>
                  {selectedCountry?.iso2 === suggestedCountry.iso2 && <CheckIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />}
                </button>
              </div>
            )}

            {countries.length === 0 && !loading ? (
              <div className="p-4 text-xs text-center text-slate-500 dark:text-slate-400">
                No matching countries found
              </div>
            ) : (
              countries.map((country, idx) => {
                const isSelected = selectedCountry?.iso2 === country.iso2;
                return (
                  <button
                    key={`addr-${country.iso2}-${idx}`}
                    type="button"
                    onClick={() => handleSelect(country)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-medium'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/40 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2 truncate">
                      <span className="text-base">{country.flag}</span>
                      <span className="truncate text-slate-900 dark:text-slate-100">
                        {country.name}
                      </span>
                    </div>
                    {isSelected && <CheckIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />}
                  </button>
                );
              })
            )}

            {loading && (
              <div className="py-2 text-center text-xs text-slate-400">
                Loading more...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
